<script setup lang="ts">
import { debounce } from "lodash-es";
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";

import {
  analysisSampleList,
  analysisSampleCreate,
  analysisSampleDelete,
} from "@/common/api/rest/analysis/sample";
import { samplePatch } from "@/common/api/rest/sample";
import { analysisMethodList } from "@/common/api/rest/analysis/method";

import type { AnalysisType } from "@/common/types/analysis";
import type { SampleListType, SamplePayloadType } from "@/common/types/sample";

import { sampleForm, searchSample } from "@/components";
import {
  baseConfirm,
  baseModal,
  baseTooltip,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: AnalysisType | null;
    width?: string;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    isEdit: true,
  },
);

const samples = ref<SampleListType[]>([]);
const methods = ref<any[]>([]);

const payload = ref<SamplePayloadType | null>(null);
const selected = ref<any>(null);
const inputSearch = ref("");

const showModalAdd = ref(false);
const showDelConfirm = ref(false);

const ids = computed(() => samples.value?.map((item) => item.sample_id) ?? []);

const methodOptions = computed(() =>
  methods.value.map((item) => ({ option: item.name, value: item.method_id })),
);

function fetchData() {
  if (!props.data?.analysis_id) {
    return;
  }

  analysisSampleList(props.data.account_id, {
    analysis_id: props.data.analysis_id,
    page: 0,
    limit: 100,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        samples.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchMethods() {
  if (!props.data) return;

  analysisMethodList(props.data.account_id, {
    analysis_id: props.data.analysis_id,
    page: 0,
    limit: 100,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        methods.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function update() {
  emit("update-data");
}

function updateSample(sample_id: string, payload: SamplePayloadType) {
  if (!props.data) return;

  samplePatch(props.data.account_id, sample_id, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        update();
        fetchData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function add() {
  showModalAdd.value = true;
}

function addSelect(data: any[]) {
  selected.value = data[0];
}

function addCancel() {
  inputSearch.value = "";
  selected.value = null;
  showModalAdd.value = false;
}

function addConfirm() {
  if (!props.data || !selected.value) return;

  console.log("selected", selected.value);

  analysisSampleCreate(props.data?.account_id, {
    evaluation_id: props.data.evaluation_id,
    analysis_id: props.data.analysis_id,
    sample_id: selected.value.sample_id,
    sample_name: selected.value.code,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        addCancel();
        update();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function del(data: SampleListType) {
  showDelConfirm.value = true;
  selected.value = data;
}

function delCancel() {
  showDelConfirm.value = false;
  selected.value = null;
}

function delConfirm() {
  if (!props.data || !selected.value) return;

  analysisSampleDelete(props.data.account_id, selected.value.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        update();
        delCancel();
        fetchData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

const debounceForm = debounce(updateSample, 1000);

watchEffect(() => {
  fetchData();
  fetchMethods();
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <div class="content">
      <template v-if="samples.length === 0">
        <div class="form-empty">
          {{ t("analysis.texts.empty_sample") }}
        </div>
      </template>

      <template v-else>
        <template v-for="(sample, index) in samples" :key="index">
          <div class="item">
            <div class="action">
              <span class="action-title"> Amostra {{ index + 1 }} </span>

              <template v-if="isEdit">
                <base-tooltip position="relative">
                  <template #trigger>
                    <button
                      type="button"
                      class="btn btn-none btn-sm remove"
                      @click="del(sample)"
                    >
                      <svg-icon icon="trash" size="1.75rem" />
                    </button>
                  </template>
                  <template #tooltip>
                    {{ t("common.labels.delete") }}
                  </template>
                </base-tooltip>
              </template>
            </div>

            <div class="infos">
              <sample-form
                :data="sample"
                :methods="methodOptions"
                :show-method="true"
                @update-data="debounceForm(sample.sample_id, $event)"
              />
            </div>
          </div>
        </template>
      </template>
    </div>

    <div class="actions">
      <button class="btn btn-md btn-primary" @click="add()">
        {{ t("common.labels.add").toUpperCase() }}
      </button>
    </div>

    <base-modal
      :show="showModalAdd"
      :title="
        t('common.labels.add', {
          concat: t('common.labels.sample'),
        }).toUpperCase()
      "
      :show-button-confirm="true"
      :button-confirm="t('common.labels.add')"
      :button-close="t('common.labels.cancel')"
      @close="addCancel()"
      @confirm="addConfirm()"
    >
      <template #content>
        <search-sample
          :account-id="data?.account_id"
          :ids="ids"
          :unic="true"
          @update-data="addSelect"
        />
      </template>
    </base-modal>

    <base-confirm
      :show="showDelConfirm"
      max-width="400px"
      :title="
        t('common.labels.remove', {
          concat: t('common.labels.sample'),
        }).toUpperCase()
      "
      :message="
        t('common.texts.remove_message', {
          concat: payload?.name,
        })
      "
      @cancel="delCancel"
      @confirm="delConfirm"
    />
  </section>
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-block: 1rem;
  padding-inline: 1.5rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border);
    padding-block-end: 0.25rem;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .item {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 1rem;
      background-color: var(--background-off);
      // box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.1);
      border-bottom: 1px solid var(--border);
      margin-bottom: 1rem;
      padding-bottom: 2.5rem;
    }
  }

  .search {
    width: 100%;

    .icon {
      cursor: pointer;
      color: var(--text-off);

      &:hover {
        color: var(--primary);
      }
    }
  }

  .border {
    border-block-end: 1px solid var(--border);
    padding-block-end: 0.4rem;
  }

  .form-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    padding-block: 1.5rem;
  }

  .actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-block: 1.5rem;
  }

  .action {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-block-end: 1rem;

    &-title {
      font-size: 1rem;
      font-weight: bold;
      text-transform: uppercase;
    }

    .remove {
      &:hover {
        color: var(--red);
      }
    }
  }
}
</style>
